// Admin → Runs: is the agent getting better? Two pass rates (the agent's own and the people's), where the time goes,
// which tools fail, which warnings come up; every run, filterable, and any one in full with the screens it saw.
import { useEffect, useMemo, useRef, useState } from "react";
import { api, type AdminRun, type RunDetail, type RunFilters, type Stats, type TargetT, type UserT } from "./api";
import { Markdown } from "./markdown";
import { StartupRow, StepRow } from "./rows";
import type { Run } from "./timeline";

const pct = (x: number | null | undefined) => (x == null ? "–" : `${Math.round(100 * x)} %`);
const secs = (x: number | null | undefined) =>
  x == null ? "–" : x < 60 ? `${x < 10 ? x.toFixed(1) : Math.round(x)} s` : `${Math.floor(x / 60)} min ${Math.round(x % 60)} s`;
const usd = (x: number | null | undefined) => (x == null ? "–" : `$${x < 10 ? x.toFixed(2) : Math.round(x)}`);
const when = (t: number) => {
  const d = new Date(t * 1000), today = new Date();
  const hm = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toDateString() === today.toDateString() ? hm : `${d.toLocaleDateString([], { month: "short", day: "numeric" })} ${hm}`;
};
const STATUS_DOT: Record<string, string> = { passed: "ok", failed: "err", error: "err", stopped: "warn", interrupted: "warn" };

export function Runs() {
  const [f, setF] = useState<RunFilters>({ days: 14 });
  const [stats, setStats] = useState<Stats | null>(null);
  const [rows, setRows] = useState<AdminRun[] | null>(null);
  const [targets, setTargets] = useState<TargetT[]>([]);
  const [users, setUsers] = useState<UserT[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [err, setErr] = useState("");

  useEffect(() => { api.targets().then(setTargets).catch(() => undefined); api.users().then(setUsers).catch(() => undefined); }, []);
  useEffect(() => {
    setErr("");
    api.stats(f).then(setStats).catch((e) => setErr(String(e.message)));
    api.adminRuns(f).then(setRows).catch((e) => setErr(String(e.message)));
  }, [f]);
  const set = (k: keyof RunFilters) => (e: React.ChangeEvent<HTMLSelectElement>) =>
    setF({ ...f, [k]: k === "days" ? Number(e.target.value) : e.target.value });

  return (
    <>
      <div className="filters">
        <select className="field" value={f.days ?? 14} onChange={set("days")} aria-label="Period">
          {[[1, "Today"], [7, "7 days"], [14, "14 days"], [30, "30 days"], [90, "90 days"], [0, "All time"]].map(([v, l]) =>
            <option key={v} value={v}>{l}</option>)}
        </select>
        <select className="field" value={f.target ?? ""} onChange={set("target")} aria-label="Computer">
          <option value="">All computers</option>
          {targets.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select className="field" value={f.user ?? ""} onChange={set("user")} aria-label="Person">
          <option value="">Everyone</option>
          {users.map((u) => <option key={u.email} value={u.email}>{u.email}</option>)}
        </select>
        <select className="field" value={f.status ?? ""} onChange={set("status")} aria-label="Outcome">
          <option value="">Any outcome</option>
          {["passed", "failed", "error", "stopped", "interrupted", "out_of_steps"].map((s) => <option key={s}>{s}</option>)}
        </select>
        <select className="field" value={f.verdict ?? ""} onChange={set("verdict")} aria-label="Verdict">
          <option value="">Any verdict</option>
          <option value="up">👍</option><option value="down">👎</option><option value="none">Not judged</option>
        </select>
      </div>
      {err && <div className="error-line">{err}</div>}
      {stats && <Kpis s={stats} />}
      {stats && stats.days.length > 0 && <DayChart days={stats.days} />}
      {stats && (
        <div className="two-col">
          <section>
            <h2>Tools</h2>
            <table className="grid">
              <thead><tr><th>Tool</th><th className="num">Calls</th><th className="num">Failed</th><th>Fail rate</th><th className="num">Median</th></tr></thead>
              <tbody>
                {stats.tools.length === 0 && <tr><td colSpan={5} className="muted">No tool calls yet.</td></tr>}
                {stats.tools.map((t) => (
                  <tr key={t.tool}>
                    <td className="mono">{t.tool}</td>
                    <td className="num">{t.calls}</td>
                    <td className="num">{t.failed || <span className="muted">0</span>}</td>
                    <td><Meter value={t.fail_rate} /></td>
                    <td className="num muted">{t.median_ms == null ? "–" : secs(t.median_ms / 1000)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          <section>
            <h2>Warnings</h2>
            <table className="grid">
              <thead><tr><th>Warning</th><th className="num">Times</th><th className="num">Runs</th></tr></thead>
              <tbody>
                {stats.warnings.length === 0 && <tr><td colSpan={3} className="muted">None.</td></tr>}
                {stats.warnings.map((w) => (
                  <tr key={w.kind}><td className="mono">{w.kind}</td><td className="num">{w.count}</td><td className="num muted">{w.runs}</td></tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      )}
      <h2>Runs</h2>
      <table className="grid runs-table">
        <thead><tr><th>When</th><th>Who</th><th>Computer</th><th>Asked</th><th>Outcome</th><th>You</th>
          <th className="num">Steps</th><th className="num">Took</th><th className="num">Ready</th><th className="num">Cost</th></tr></thead>
        <tbody>
          {rows === null && <tr><td colSpan={10} className="muted">Loading…</td></tr>}
          {rows?.length === 0 && <tr><td colSpan={10} className="muted">No runs match.</td></tr>}
          {rows?.map((r) => (
            <tr key={r.id} className={`clickable ${open === r.id ? "selected" : ""}`} onClick={() => setOpen(open === r.id ? null : r.id)}>
              <td className="muted">{when(r.started_at)}</td>
              <td>{r.owner.split("@")[0]}</td>
              <td>{r.target}</td>
              <td className="ask" title={r.message}>{r.message}</td>
              <td><span className={`dot ${STATUS_DOT[r.status] ?? ""}`} /> {r.status}</td>
              <td title={r.verdict_note ?? ""}>{r.verdict === "up" ? "👍" : r.verdict === "down" ? "👎" : <span className="muted">–</span>}</td>
              <td className="num">{r.steps ?? "–"}</td>
              <td className="num">{secs(r.seconds)}</td>
              <td className="num muted">{secs(r.ready_s)}</td>
              <td className="num muted">{usd(r.cost_usd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {open && <RunPanel rid={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function Kpis({ s }: { s: Stats }) {
  const tiles: [string, string, string?][] = [
    ["Runs", String(s.runs)],
    ["Passed, says the agent", pct(s.agent_pass_rate)],
    ["Passed, say people", pct(s.people_pass_rate), s.judged ? `${s.judged} judged` : "none judged yet"],
    ["Agent passed, person 👎", String(s.agent_passed_people_down)],
    ["Median run", secs(s.median_seconds)],
    ["Per step", secs(s.median_step_s)],
    ["Session ready", secs(s.ready_median_s), s.ready_p90_s != null ? `p90 ${secs(s.ready_p90_s)}` : undefined],
    ["Cost", usd(s.cost_usd)],
  ];
  return (
    <div className="kpis">
      {tiles.map(([label, value, sub]) => (
        <div key={label} className="kpi">
          <div className="label">{label}</div>
          <div className="kpi-value">{value}</div>
          {sub && <div className="kpi-sub">{sub}</div>}
        </div>
      ))}
    </div>
  );
}

// Runs per day: one series, one colour; the passed share is in the tooltip and the table, not a second colour.
function DayChart({ days: given }: { days: Stats["days"] }) {
  const [hover, setHover] = useState<number | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(720);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(Math.max(240, Math.round(el.clientWidth))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const days = useMemo(() => fillDays(given), [given]);
  const H = 120, pad = { l: 28, r: 4, t: 8, b: 18 };
  const max = Math.max(1, ...days.map((d) => d.runs));
  const step = niceStep(max);
  const top = Math.ceil(max / step) * step;
  const band = (W - pad.l - pad.r) / days.length;
  const bw = Math.min(24, band * 0.7);
  const y = (v: number) => pad.t + (H - pad.t - pad.b) * (1 - v / top);
  const ticks = useMemo(() => Array.from({ length: Math.floor(top / step) + 1 }, (_, i) => i * step), [top, step]);
  const every = Math.ceil(days.length / 8);
  const h = hover != null ? days[hover] : null;
  return (
    <figure className="chart">
      <figcaption className="label">Runs per day</figcaption>
      <div className="chart-box" ref={box}>
        <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label="Runs per day" onPointerLeave={() => setHover(null)}>
          {ticks.map((t) => (
            <g key={t}>
              <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} stroke="var(--rule)" strokeWidth={1} />
              <text x={pad.l - 6} y={y(t) + 3} textAnchor="end" className="axis">{t}</text>
            </g>
          ))}
          {days.map((d, i) => {
            const x = pad.l + band * i + (band - bw) / 2, top_ = y(d.runs), base = y(0), r = Math.min(4, bw / 2, base - top_);
            return (
              <g key={d.day} onPointerEnter={() => setHover(i)}>
                <rect x={pad.l + band * i} y={pad.t} width={band} height={H - pad.t - pad.b} fill="transparent" />
                {d.runs > 0 && (
                  <path d={`M${x},${base}V${top_ + r}Q${x},${top_} ${x + r},${top_}H${x + bw - r}Q${x + bw},${top_} ${x + bw},${top_ + r}V${base}Z`}
                    fill="var(--chart)" opacity={hover == null || hover === i ? 1 : 0.55} />
                )}
                {i % every === 0 && <text x={x + bw / 2} y={H - 4} textAnchor="middle" className="axis">{d.day.slice(5)}</text>}
              </g>
            );
          })}
        </svg>
        {h && (
          <div className="tip" style={{ left: `${((pad.l + band * (hover! + 0.5)) / W) * 100}%` }}>
            <strong>{h.runs}</strong> runs · {h.passed} passed · {usd(h.cost_usd)}<div className="muted">{h.day}</div>
          </div>
        )}
      </div>
    </figure>
  );
}

// Every day of the period, a day without runs as zero: the axis is time, gaps are information.
function fillDays(days: Stats["days"]) {
  if (days.length === 0) return days;
  const by = new Map(days.map((d) => [d.day, d]));
  const out: Stats["days"] = [];
  const end = new Date(`${days[days.length - 1].day}T12:00:00`);
  for (let d = new Date(`${days[0].day}T12:00:00`); d <= end; d.setDate(d.getDate() + 1)) {
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    out.push(by.get(key) ?? { day: key, runs: 0, passed: 0, cost_usd: 0 });
  }
  return out;
}

function niceStep(max: number) {
  const raw = max / 3, mag = 10 ** Math.floor(Math.log10(raw)), n = raw / mag;
  return Math.max(1, (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * mag);
}

function Meter({ value }: { value: number }) {
  return (
    <span className="meter-cell">
      <span className="meter-track"><span className="meter-fill" style={{ width: `${Math.max(value > 0 ? 3 : 0, 100 * value)}%` }} /></span>
      <span className="num">{pct(value)}</span>
    </span>
  );
}

function RunPanel({ rid, onClose }: { rid: string; onClose: () => void }) {
  const [d, setD] = useState<RunDetail | null>(null);
  useEffect(() => { setD(null); api.adminRun(rid).then(setD).catch(() => undefined); }, [rid]);
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onClose]);
  if (!d) return <aside className="run-panel"><div className="muted" style={{ padding: 12 }}>Loading…</div></aside>;
  const r = d.run, startup = d.record.startup as Record<string, number> | undefined;
  return (
    <aside className="run-panel">
      <header className="run-panel-head">
        <span className="label">Run</span>
        <span className="grow mono muted">{r.id}</span>
        {d.files && <a className="btn quiet" href={`/api/admin/runs/${rid}/zip`}>Download</a>}
        <button className="btn quiet" onClick={onClose} aria-label="Close">✕</button>
      </header>
      <div className="run-panel-body">
        <div className="msg-user">{r.message}</div>
        <div className="kv small">
          <span className="label">Who</span><span>{r.owner} on {r.target}</span>
          <span className="label">Outcome</span><span><span className={`dot ${STATUS_DOT[r.status] ?? ""}`} /> {r.status}
            {r.verdict && <> · person {r.verdict === "up" ? "👍" : "👎"}{r.verdict_note ? `: ${r.verdict_note}` : ""}</>}</span>
          <span className="label">Model</span><span>{r.model ?? "–"}</span>
          <span className="label">Time</span><span>{secs(r.seconds)} · ready after {secs(r.ready_s)}{startup &&
            ` (${Object.entries(startup).map(([k, v]) => `${k} ${secs(v)}`).join(", ")})`}</span>
          <span className="label">Cost</span><span>{usd(r.cost_usd)}</span>
        </div>
        {!d.files && <p className="note">This run's screens and events were cleaned up; its steps remain.</p>}
        <h3 className="label">Timeline</h3>
        {(() => {
          // the same rows as the chat: each opens to what it did (read from the run's events), its result, its screen
          const run: Run = { id: rid, conversation: "", message: r.message, items: [], started: r.started_at * 1000, seq: 0,
            ahead: [], think: { on: false, since: 0 }, stages: {}, lastAt: 0, status: r.status, readyS: r.ready_s };
          return (
            <>
              <StartupRow run={run} readyS={r.ready_s} />
              {d.steps.map((s) => s.tool === "" ? <div key={s.n} className="msg-text"><Markdown text={s.text} /></div>
                : <StepRow key={s.n} run={run} live={false} s={{ n: s.n, kind: "tool", tool: s.tool, input: s.input,
                    verdict: s.verdict, text: s.text, ms: s.ms ?? undefined, shot: d.files ? s.shot ?? undefined : undefined,
                    done: true, skipped: s.verdict === "skipped", act: { lines: [], hidden: 0 } }} />)}
            </>
          );
        })()}
        <div className="summary-line"><Markdown text={r.summary || ""} /></div>
        {d.takeovers.length > 0 && <>
          <h3 className="label">Taken over</h3>
          {d.takeovers.map((t, i) => (
            <div key={i} className="small muted">{String(t.seconds ?? "?")} s · {String(t.clicks ?? 0)} clicks · {String(t.keys ?? 0)} keys ·
              {" "}{String(t.typed_chars ?? 0)} characters typed (not kept)</div>
          ))}
        </>}
        {d.warnings.length > 0 && <>
          <h3 className="label">Warnings ({d.warnings.length})</h3>
          <div className="warn-list mono">
            {d.warnings.slice(0, 100).map((w, i) => (
              <div key={i}><span className="muted">{Number(w.t).toFixed(1)}s</span> {String(w.kind)}{" "}
                <span className="muted">{Object.entries(w).filter(([k]) => !["t", "kind", "level", "phase"].includes(k))
                  .map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`).join(" ").slice(0, 160)}</span></div>
            ))}
          </div>
        </>}
      </div>
    </aside>
  );
}
